import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiSave, FiX, FiBell } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { API_ENDPOINTS } from '../config/api';
import apiClient, { endpoints } from '../api/client';
// Removed ReactQuill due to React 19 compatibility issues
import DeleteItemModal from '../components/DeleteItemModal';

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const { isSuperAdmin } = useAuth();
  const { showToast } = useToast();
  const [showDeleteModal, setShowDeleteModal] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body_html: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [validationWarnings, setValidationWarnings] = useState({});

  // Allowed template variables for client-side validation
  const ALLOWED_VARIABLES = [
    'first_name', 'last_name', 'email',
    'certificate_type', 'expiry_date',
    'days_left', 'days_until_expiry', 'invitation_url'
  ];

  const supportedVariables = ALLOWED_VARIABLES.map(variable => `{{${variable}}}`);

  // Client-side validation functions
  const validateTemplateVariables = (text) => {
    if (!text) return { invalidVars: [], suspiciousPatterns: [] };
    
    const variables = text.match(/{{\s*([^}]+)\s*}}/g) || [];
    const invalidVars = [];
    const suspiciousPatterns = [];
    
    variables.forEach(variable => {
      const varName = variable.replace(/[{}\s]/g, '').toLowerCase();
      
      if (!ALLOWED_VARIABLES.includes(varName)) {
        invalidVars.push(varName);
      }
      
      if (/[<>"'`\\]/.test(varName) || varName.includes('script') || varName.includes('javascript')) {
        suspiciousPatterns.push(varName);
      }
    });
    
    return { invalidVars, suspiciousPatterns };
  };

  const validateTemplateName = (name) => {
    if (!name || typeof name !== 'string') {
      return { valid: false, error: 'Template name is required' };
    }
    
    if (name.length < 3 || name.length > 100) {
      return { valid: false, error: 'Template name must be between 3 and 100 characters' };
    }
    
    if (!/^[a-zA-Z0-9_\-\s]+$/.test(name)) {
      return { valid: false, error: 'Template name can only contain letters, numbers, underscores, hyphens, and spaces' };
    }
    
    return { valid: true };
  };

  const validateFormData = (data) => {
    const errors = {};
    const warnings = {};
    
    // Validate name
    const nameValidation = validateTemplateName(data.name);
    if (!nameValidation.valid) {
      errors.name = nameValidation.error;
    }
    
    // Validate subject variables
    const subjectValidation = validateTemplateVariables(data.subject);
    if (subjectValidation.invalidVars.length > 0) {
      errors.subject = `Invalid variables: ${subjectValidation.invalidVars.join(', ')}`;
    }
    if (subjectValidation.suspiciousPatterns.length > 0) {
      errors.subject = `Suspicious patterns detected: ${subjectValidation.suspiciousPatterns.join(', ')}`;
    }
    
    // Validate body variables
    const bodyValidation = validateTemplateVariables(data.body_html);
    if (bodyValidation.invalidVars.length > 0) {
      errors.body_html = `Invalid variables: ${bodyValidation.invalidVars.join(', ')}`;
    }
    if (bodyValidation.suspiciousPatterns.length > 0) {
      errors.body_html = `Suspicious patterns detected: ${bodyValidation.suspiciousPatterns.join(', ')}`;
    }
    
    return { errors, warnings };
  };

  const sampleData = {
    user_name: 'John Doe',
    certificate_type: 'CPR Certification',
    expiry_date: '2024-12-31',
    days_left: '30'
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`${API_ENDPOINTS.EMAIL_TEMPLATES}?sort_by=created_at&sort_order=ASC`);
      if (response.data.success) {
        setTemplates(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching email templates:', error);
      showToast('Failed to fetch email templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    const validation = validateFormData(formData);
    setValidationErrors(validation.errors);
    setValidationWarnings(validation.warnings);
    
    if (Object.keys(validation.errors).length > 0) {
      return; // Don't submit if there are validation errors
    }
    
    // Validate variables
    const bodyText = formData.body_html.replace(/<[^>]*>/g, '');
    const variableRegex = /{{([^}]+)}}/g;
    const matches = bodyText.match(variableRegex) || [];
    
    for (const match of matches) {
      if (!supportedVariables.includes(match)) {
        alert(`Unsupported variable: ${match}. Supported variables: ${supportedVariables.join(', ')}`);
        return;
      }
    }

    try {
      const url = editingTemplate 
        ? API_ENDPOINTS.EMAIL_TEMPLATE_BY_ID(editingTemplate.id)
        : API_ENDPOINTS.EMAIL_TEMPLATES;
      
      const method = editingTemplate ? 'put' : 'post';
      
      const response = await apiClient[method](url, formData);

      if (response.data.success) {
        fetchTemplates();
        resetForm();
        setValidationErrors({});
        setValidationWarnings({});
        showToast(
          editingTemplate ? 'Email template updated successfully' : 'Email template created successfully',
          'success'
        );
      }
    } catch (error) {
      console.error('Error saving template:', error);
      // Handle server-side validation errors
      if (error.response?.data?.message) {
        if (error.response.data.message.includes('Invalid variables')) {
          setValidationErrors({ general: error.response.data.message });
        } else if (error.response.data.message.includes('Suspicious patterns')) {
          setValidationErrors({ general: error.response.data.message });
        } else {
          setValidationErrors({ general: error.response.data.message });
        }
      }
      const errorMessage = error.response?.data?.message || 'Failed to save email template';
      showToast(errorMessage, 'error');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      body_html: template.body_html
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await apiClient.delete(API_ENDPOINTS.EMAIL_TEMPLATE_BY_ID(id));
      showToast('Email template deleted successfully', 'success');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      showToast('Failed to delete email template', 'error');
    }
  };

  const handlePreview = async (template) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.EMAIL_TEMPLATE_PREVIEW(template.id), {
        variables: sampleData
      });
      
      if (response.data.success) {
        setPreviewTemplate(template);
        setPreviewData(response.data.data);
        setShowPreview(true);
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      showToast('Failed to generate preview', 'error');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', subject: '', body_html: '' });
    setEditingTemplate(null);
    setShowForm(false);
  };

  const renderPreview = () => {
    if (!previewTemplate) return '';
    return previewTemplate.body_html || '';
  };

  // Simple HTML formatting helper functions
  const insertHtmlTag = (tag, textarea) => {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const beforeText = textarea.value.substring(0, start);
    const afterText = textarea.value.substring(end);
    
    let newText;
    if (selectedText) {
      newText = `${beforeText}<${tag}>${selectedText}</${tag}>${afterText}`;
    } else {
      newText = `${beforeText}<${tag}></${tag}>${afterText}`;
    }
    
    return newText;
  };

  const handleFormatting = (tag) => {
    const textarea = document.getElementById('body-textarea');
    if (textarea) {
      const newValue = insertHtmlTag(tag, textarea);
      setFormData(prev => ({ ...prev, body_html: newValue }));
      
      // Real-time validation for editor content
      const updatedData = { ...formData, body_html: newValue };
      const validation = validateFormData(updatedData);
      setValidationErrors(validation.errors);
      setValidationWarnings(validation.warnings);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Premier Choice Branded Header */}
      <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-4">
              <span className="text-white text-xl font-bold">PR</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold">Email Templates</h1>
              <p className="mt-1 text-red-100">
                Premier Choice CRM - Manage email templates for automated notifications and communications.
              </p>
            </div>
          </div>
          {/* {isSuperAdmin() && (
            <button
              onClick={() => setShowForm(true)}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-lg flex items-center space-x-2 btn-animate transition-all duration-200"
            >
              <FiPlus className="h-4 w-4" />
              <span>Add Template</span>
            </button>
          )} */}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <FiBell className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Email Templates
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Customize and manage your email templates for reminders and notifications.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Templates Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {templates.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No email templates found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Updated At
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {templates.map((template) => (
                    <tr key={template.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {template.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {template.subject}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(template.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        <button
                          onClick={() => handlePreview(template)}
                          className="inline-flex items-center px-2 py-1 text-white hover:text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg btn-animate transition-all duration-200 mr-2"
                          title="Preview Template"
                        >
                          <FiEye className="h-4 w-4" />
                        </button>
                        {isSuperAdmin() && (
                          <>
                            <button
                              onClick={() => handleEdit(template)}
                              className="inline-flex items-center px-2 py-1 text-white hover:text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg btn-animate transition-all duration-200 mr-2"
                              title="Edit Template"
                            >
                              <FiEdit className="h-4 w-4" />
                            </button>
                            {/* <button
                              onClick={() => setShowDeleteModal(template.id)}
                              className="inline-flex items-center px-2 py-1 text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 rounded-lg transition-all duration-200"
                              title="Delete Template"
                            >
                              <FiTrash2 className="h-4 w-4" />
                            </button> */}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-lg bg-white">
            {/* Branded Modal Header */}
            <div className="bg-gradient-to-r from-brand-primary to-brand-primary-dark -m-5 mb-6 p-6 rounded-t-lg">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-3 p-1">
                    <img 
                      src={"https://premierchoicecpr.com/wp-content/uploads/2022/02/cropped-Premier_Choice_CPR.cdr_1.cdr_final-removebg-preview.png"} 
                      alt="Premier Choice Logo" 
                      className="w-full h-full object-contain animate-fade-in"
                      style={{
                        filter: 'drop-shadow(0 0 2px rgba(255, 255, 255, 0.8))',
                        border: '1px solid rgba(255, 255, 255, 0.3)'
                      }}
                    />
                  </div>
                  <h3 className="text-lg font-medium text-white">
                    {editingTemplate ? 'Edit Email Template' : 'Add Email Template'}
                  </h3>
                </div>
                <button
                  onClick={resetForm}
                  className="text-white hover:text-red-200 transition-colors"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => {
                    const { name, value } = e.target;
                    setFormData(prev => ({ ...prev, [name]: value }));
                    
                    // Real-time validation
                    const updatedData = { ...formData, [name]: value };
                    const validation = validateFormData(updatedData);
                    setValidationErrors(validation.errors);
                    setValidationWarnings(validation.warnings);
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 ${
                    validationErrors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {validationErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject Line
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={(e) => {
                    const { name, value } = e.target;
                    setFormData(prev => ({ ...prev, [name]: value }));
                    
                    // Real-time validation
                    const updatedData = { ...formData, [name]: value };
                    const validation = validateFormData(updatedData);
                    setValidationErrors(validation.errors);
                    setValidationWarnings(validation.warnings);
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 ${
                    validationErrors.subject ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {validationErrors.subject && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.subject}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Body
                </label>
                <div className="mb-2">
                  <p className="text-xs text-gray-500">
                    Supported variables: {supportedVariables.join(', ')}
                  </p>
                </div>
                {/* HTML Formatting Toolbar */}
                <div className="mb-2 flex flex-wrap gap-2 p-2 bg-gray-50 border border-gray-300 rounded-t-lg">
                  <button
                    type="button"
                    onClick={() => handleFormatting('strong')}
                    className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-100 font-bold"
                    title="Bold"
                  >
                    B
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFormatting('em')}
                    className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-100 italic"
                    title="Italic"
                  >
                    I
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFormatting('u')}
                    className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-100 underline"
                    title="Underline"
                  >
                    U
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFormatting('h2')}
                    className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Heading"
                  >
                    H2
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFormatting('p')}
                    className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-100"
                    title="Paragraph"
                  >
                    P
                  </button>
                </div>
                <textarea
                  id="body-textarea"
                  name="body"
                  value={formData.body_html}
                  onChange={(e) => {
                    const { value } = e.target;
                    setFormData(prev => ({ ...prev, body_html: value }));
                    
                    // Real-time validation for editor content
                    const updatedData = { ...formData, body_html: value };
                    const validation = validateFormData(updatedData);
                    setValidationErrors(validation.errors);
                    setValidationWarnings(validation.warnings);
                  }}
                  className={`w-full px-3 py-2 border border-t-0 rounded-b-lg focus:outline-none focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 font-mono text-sm ${
                    validationErrors.body_html ? 'border-red-300' : 'border-gray-300'
                  }`}
                  rows="10"
                  placeholder="Enter HTML content for the email body..."
                  required
                />
                {validationErrors.body_html && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.body_html}</p>
                )}
              </div>

              {validationErrors.general && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{validationErrors.general}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary btn-animate transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-brand-primary hover:bg-brand-primary-dark text-white px-4 py-2 rounded-lg flex items-center space-x-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary btn-animate transition-all duration-200"
                >
                  <FiSave className="h-4 w-4" />
                  <span>{editingTemplate ? 'Update' : 'Save'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-lg bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Preview: {previewTemplate.name}
              </h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>

            <div className="border rounded-lg overflow-hidden">
              {/* Email Header */}
              <div className="bg-brand-primary text-white p-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-white rounded flex items-center justify-center mr-3 p-1">
                    <img 
                      src={"https://premierchoicecpr.com/wp-content/uploads/2022/02/cropped-Premier_Choice_CPR.cdr_1.cdr_final-removebg-preview.png"} 
                      alt="Premier Choice Logo" 
                      className="w-full h-full object-contain animate-fade-in"
                      style={{
                        filter: 'drop-shadow(0 0 1px rgba(0, 0, 0, 0.2))',
                        border: '1px solid rgba(255, 4, 4, 0.2)'
                      }}
                    />
                  </div>
                  <span className="text-lg font-semibold">Premier Choice</span>
                </div>
              </div>

              {/* Email Content */}
              <div className="p-6 bg-white">
                <h2 className="text-xl font-semibold mb-4">{previewTemplate.subject}</h2>
                <div 
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: renderPreview() }}
                />
              </div>
            </div>

            {/* <div className="mt-4 text-xs text-gray-500">
              <p>Preview uses sample data: {JSON.stringify(sampleData, null, 2)}</p>
            </div> */}
          </div>
        </div>
      )}

      {/* Delete Template Modal */}
      {showDeleteModal !== null && (
        <DeleteItemModal
          entity="template"
          url={API_ENDPOINTS.EMAIL_TEMPLATE_BY_ID(showDeleteModal)}
          isOpen={showDeleteModal !== null}
          onClose={() => setShowDeleteModal(null)}
          onItemDeleted={() => fetchTemplates()}
        />
      )}
    </div>
  );
};

export default EmailTemplates;